/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import { Alert, Button, Card, FieldGroup, Input, Select, SvgIcon, Switch, Table } from "core/components";
import formatter from "core/formatter";
import { navigateTo } from "core/navigation";
import moment from "moment";
import {
  admin_purchase_orders_path,
  delivery_site_purchase_order_path,
  admin_purchase_order_path,
  release_admin_purchase_order_path,
  pending_site_deliveries_path,
  cancel_admin_purchase_order_path,
  update_price_admin_purchase_order_path,
  print_admin_purchase_order_path,
  close_partially_delivered_admin_purchase_order_path
} from "routes";
import { PurchaseOrderStatus, PurchaseOrderSchema, PurchaseOrderItemFormData, InventoryItemFormData } from "core/schema";
import { IMaskInput } from "react-imask";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import ItemPriceUpdate from "./ItemPriceUpdate";
import PurchaseOrderContext from "./PurchaseOrderContext";

type PurchaseOrderFormProps = {
  purchase_orders: Array<Record<string, unknown>>;
};

const SelectField = ({ inventory_items, setValue, control, watch, props, purchase_order }) => {
  const { selectedItems, setSelectedItems } = React.useContext(PurchaseOrderContext);
  const fieldArrayName = "purchase_order_items";
  const index = props.row.index;
  const fieldItem = `${fieldArrayName}.${index}`;
  const purchaseOrderItem = watch(fieldItem);
  const [selectedItem, setSelectedItem] = React.useState<InventoryItemFormData>(
    inventory_items.find((inventory_item) => inventory_item.purchase_request_item_id == purchaseOrderItem?.purchase_request_item_id)
  );

  const addToSelectedItems = (item) => {
    if (selectedItems.find((i) => i.purchase_request_item_id == item.purchase_request_item_id)) {
      return;
    } else {
      setSelectedItems((value) => [...value, item]);
    }
  };

  const removeFromSelectedItems = (item) => {
    if (selectedItems.find((selectedItem) => selectedItem.purchase_request_item_id == item?.purchase_request_item_id)) {
      setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.purchase_request_item_id != item?.purchase_request_item_id));
    }
  };

  React.useEffect(() => {
    if (selectedItem) {
      addToSelectedItems(selectedItem);
    }
  }, [selectedItem]);

  return (
    <Controller
      name={`${fieldArrayName}.${index}.item_id`}
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          placeholder="Select Item"
          options={inventory_items.filter(
            (inventory_item) =>
              !selectedItems.find(
                (selected) =>
                  selected.purchase_request_item_id == inventory_item.purchase_request_item_id &&
                  field?.value != inventory_item.purchase_request_item_id
              ) || null
          )}
          getOptionLabel={(option) => `${option.code} - ${option.name}`}
          getOptionValue={(option) => option.id}
          isOptionDisabled={(option) => !option.active}
          onChange={(option, actionMeta) => {
            field.onChange(option?.id);

            if (selectedItem) {
              removeFromSelectedItems(selectedItem);
            }

            setSelectedItem(option);

            if (actionMeta.action == "select-option") {
              if (purchase_order.subdivision_code) {
                setValue(`${fieldItem}.project_code`, option?.project_code);
                setValue(`${fieldItem}.subdivision_code`, purchase_order.subdivision_code);
                setValue(`${fieldItem}.phase`, option?.phase);
                setValue(`${fieldItem}.block`, option?.block);
                setValue(`${fieldItem}.lot`, option?.lot);
              }
              setValue(`${fieldItem}.purchase_request_item_id`, option?.purchase_request_item_id);
              setValue(`${fieldItem}.requested_quantity`, option?.requested_quantity);
              setValue(`${fieldItem}.drafted_quantity`, option?.drafted_quantity);
              setValue(`${fieldItem}.ordered_quantity`, option?.ordered_quantity);
              setValue(`${fieldItem}.unit_measure_code`, option?.unit_measure?.code);
              setValue(`${fieldItem}.unit_price`, option?.unit_price);
              setValue(`${fieldItem}.purchase_request_code`, option?.purchase_request_code);
              addToSelectedItems(option);
            } else {
              if (purchase_order.subdivision_code) {
                setValue(`${fieldItem}.project_code`, null);
                setValue(`${fieldItem}.subdivision_code`, null);
                setValue(`${fieldItem}.phase`, null);
                setValue(`${fieldItem}.block`, null);
                setValue(`${fieldItem}.lot`, null);
              }
              setValue(`${fieldItem}.purchase_request_item_id`, null);
              setValue(`${fieldItem}.requested_quantity`, 0);
              setValue(`${fieldItem}.drafted_quantity`, 0);
              setValue(`${fieldItem}.ordered_quantity`, 0);
              setValue(`${fieldItem}.unit_measure_code`, null);
              setValue(`${fieldItem}.unit_price`, 0);
              setValue(`${fieldItem}.purchase_request_code`, null);
              removeFromSelectedItems(option || actionMeta?.removedValues[0]);
            }
          }}
          ref={field.ref}
          value={inventory_items.find((inventory_item) => inventory_item.id == field.value) || null}
          errorMessage={props.errors[fieldArrayName]?.[index]?.item_id?.message}
        />
      )}
    />
  );
};

const PurchaseOrderForm = ({ purchase_order, can_receive, can_place_order }: PurchaseOrderFormProps): JSX.Element => {
  const {
    control,
    formState: { isSubmitting, isValidating, errors },
    handleSubmit,
    getValues,
    watch,
    setValue,
    register,
    reset,
    resetField
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(PurchaseOrderSchema),
    defaultValues: purchase_order
  });
  const fieldArrayName = "purchase_order_items";
  const { fields, append, remove } = useFieldArray<PurchaseOrderItemFormData>({
    control,
    name: fieldArrayName,
    keyName: "key"
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [purchaseOrderItems, setPurchaseOrderItems] = React.useState(purchase_order.purchase_order_items);
  const isSiteForm = window.location.href.includes("site");
  const vatablePurchasesRate = parseFloat(purchase_order.vatable_purchases_rate);
  const valueAddedTaxRate = parseFloat(purchase_order.value_added_tax_rate);
  const witholdingTaxRate = parseFloat(purchase_order.withholding_tax_rate);
  const [subtotal, setSubtotal] = React.useState(parseFloat(purchase_order.subtotal_amount));
  const [vatablePurchases, setVatablePurchases] = React.useState(parseFloat(purchase_order.vatable_purchases_amount));
  const [valueAddedTax, setValueAddedTax] = React.useState(parseFloat(purchase_order.value_added_tax_amount));
  const [witholdingTax, setWitholdingTax] = React.useState(parseFloat(purchase_order.withholding_tax_amount));
  const [total, setTotal] = React.useState(parseFloat(purchase_order.total_amount));
  const [showPlaceOrderConfirmationModal, setShowPlaceOrderConfirmationModal] = React.useState(false);
  const [showCancelOrderConfirmationModal, setShowCancelOrderConfirmationModal] = React.useState(false);
  const [showCloseOrderConfirmationModal, setShowCloseOrderConfirmationModal] = React.useState(false);
  const reorderingColumns = purchaseOrderItems[0]?.project_code ? [2, 3] : [1, 2, 3, 4, 5];
  const draftColumns = purchase_order.status == PurchaseOrderStatus.Draft ? ["delivered_quantity", "remaining_quantity"] : ["order_quantity"];
  const amountColumns = isSiteForm ? ["unit_price", "total"] : [];
  const [purchaseOrderItem, setPurchaseOrderItem] = React.useState(null);
  const [showUpdateUnitPriceModal, setShowUpdateUnitPriceModal] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState(purchaseOrderItems);
  const [itemsForDestruction, setItemsForDestruction] = React.useState<Array<PurchaseOrderItemFormData>>([]);

  const calculateSubtotal = () => {
    const reducer = (accumulator, current) => {
      return parseFloat(accumulator) + parseFloat(current["quantity"]) * parseFloat(current["unit_price"]);
    };

    return getValues(fieldArrayName).reduce(reducer, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    let vatablePurchases;
    let valueAddedTax;
    let witholdingTax;

    if (purchase_order.supplier.tax_category == "non_vatable") {
      valueAddedTax = subtotal * valueAddedTaxRate;
      witholdingTax = subtotal * witholdingTaxRate;
    } else {
      vatablePurchases = subtotal / vatablePurchasesRate;
      valueAddedTax = vatablePurchases * valueAddedTaxRate;
      witholdingTax = vatablePurchases * witholdingTaxRate;
    }
    const total = subtotal - witholdingTax;

    setSubtotal(subtotal || 0);
    setVatablePurchases(vatablePurchases || 0);
    setValueAddedTax(valueAddedTax || 0);
    setWitholdingTax(witholdingTax || 0);
    setTotal(total || 0);
  };

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Inventory ID",
        accessor: "item_code",
        style: {
          width: "18%"
        },
        Cell: (props) => {
          const id = props.row.values.id;

          if (!id)
            return (
              <SelectField
                inventory_items={purchase_order.purchase_request_items.map((item) => {
                  return {
                    ...item.item,
                    purchase_request_item_id: item.id,
                    requested_quantity: item.requested_quantity,
                    drafted_quantity: item.drafted_quantity,
                    ordered_quantity: item.ordered_quantity,
                    unit_price: item.unit_price,
                    purchase_request_code: item.purchase_request_code,
                    project_code: item.project?.code,
                    phase: item.project?.phase,
                    block: item.project?.block,
                    lot: item.project?.lot
                  };
                })}
                setValue={setValue}
                control={control}
                watch={watch}
                props={props}
                purchase_order={purchase_order}
              />
            );

          return (
            <>
              <div title={props.value} data-bs-toggle="tooltip">
                <p className="text-truncate text-bold">{props.value}</p>
              </div>
              <div title={props.row.original.item_name} data-bs-toggle="tooltip">
                <p className="text-truncate">{props.row.original.item_name}</p>
              </div>
            </>
          );
        }
      },
      {
        id: 1,
        Header: "Project ID",
        accessor: "project_code",
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div title={props.value || getValues(`${fieldArrayName}.${index}.project_code`)} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || getValues(`${fieldArrayName}.${index}.project_code`)}</p>
            </div>
          );
        }
      },
      {
        id: 2,
        Header: "Subdivision",
        accessor: "subdivision_code",
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div title={props.value || getValues(`${fieldArrayName}.${index}.subdivision_code`)} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value || getValues(`${fieldArrayName}.${index}.subdivision_code`)}</p>
            </div>
          );
        }
      },
      {
        id: 3,
        Header: "Phase",
        accessor: "phase",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div title={props.value || getValues(`${fieldArrayName}.${index}.phase`)} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || getValues(`${fieldArrayName}.${index}.phase`) || "-"}</p>
            </div>
          );
        }
      },
      {
        id: 4,
        Header: "Block",
        accessor: "block",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div title={props.value || getValues(`${fieldArrayName}.${index}.block`)} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || getValues(`${fieldArrayName}.${index}.block`) || "-"}</p>
            </div>
          );
        }
      },
      {
        id: 5,
        Header: "Lot",
        accessor: "lot",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div title={props.value || getValues(`${fieldArrayName}.${index}.lot`)} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || getValues(`${fieldArrayName}.${index}.lot`) || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "PR No.",
        accessor: "purchase_request_code",
        Cell: (props) => {
          const index = props.row.index;
          const purchaseRequestCode = props.value || getValues(`${fieldArrayName}.${index}.purchase_request_code`);

          return (
            <div title={purchaseRequestCode} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{purchaseRequestCode}</p>
            </div>
          );
        }
      },
      {
        Header: "Requested Quantity",
        accessor: "requested_quantity",
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div title={props.value || getValues(`${fieldArrayName}.${index}.requested_quantity`)} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || getValues(`${fieldArrayName}.${index}.requested_quantity`)}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div title={props.value || getValues(`${fieldArrayName}.${index}.ordered_quantity`)} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || getValues(`${fieldArrayName}.${index}.ordered_quantity`)}</p>
            </div>
          );
        }
      },
      {
        id: "delivered_quantity",
        Header: "Delivered Quantity",
        accessor: "delivered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "remaining_quantity",
        Header: "Remaining Quantity",
        accessor: "remaining_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "order_quantity",
        Header: "Order Quantity",
        accessor: "quantity",
        style: {
          width: "10%"
        },
        Cell: (props) => {
          const index = props.row.index;
          if (purchase_order.status == PurchaseOrderStatus.Draft) {
            const orderedQuantity = parseFloat(props.row.original.ordered_quantity || getValues(`${fieldArrayName}.${index}.ordered_quantity`));
            const draftedQuantity = parseFloat(props.row.original.drafted_quantity || getValues(`${fieldArrayName}.${index}.drafted_quantity`));
            const requestedQuantity = parseFloat(props.row.original.requested_quantity || getValues(`${fieldArrayName}.${index}.requested_quantity`));
            const maxOrderQuantity = requestedQuantity - (orderedQuantity + draftedQuantity);

            return (
              <Controller
                name={`${fieldArrayName}.${props.row.index}.quantity`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FieldGroup errorMessage={error?.message}>
                    <IMaskInput
                      mask={Number}
                      padFractionalZeros={true}
                      thousandsSeparator=","
                      radix="."
                      value={field.value > 0 ? field.value.toString() : maxOrderQuantity.toString()}
                      unmask={true}
                      ref={field.ref}
                      onAccept={(value) => {
                        field.onChange(value);
                        calculateTotal();
                      }}
                      placeholder={maxOrderQuantity.toString()}
                      className="field"
                      min={0.1}
                      max={maxOrderQuantity}
                    />
                  </FieldGroup>
                )}
              />
            );
          } else {
            return (
              <div title={props.value} data-bs-toggle="tooltip">
                <p className="text-truncate text-center">{props.value}</p>
              </div>
            );
          }
        }
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: (props) => {
          if (purchase_order.status == PurchaseOrderStatus.Draft) {
            return <Input {...register(`${fieldArrayName}.${props.row.index}.remarks`)} />;
          } else {
            return (
              <div title={props.value} data-bs-toggle="tooltip">
                <p className="text-truncate text-center">{props.value}</p>
              </div>
            );
          }
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          const index = props.row.index;
          return (
            <div title={props.value || getValues(`${fieldArrayName}.${index}.unit_measure_code`)} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value || getValues(`${fieldArrayName}.${index}.unit_measure_code`)}</p>
            </div>
          );
        }
      },
      {
        id: "unit_price",
        Header: "Unit Price",
        accessor: "unit_price",
        Cell: (props) => {
          const index = props.row.index;
          const unitPrice = formatter.format(props.value || getValues(`${fieldArrayName}.${index}.unit_price`));

          return (
            <Container>
              <Row className="align-items-center align-content-center">
                <Col>
                  <div title={unitPrice} data-bs-toggle="tooltip">
                    <p className="text-truncate text-right">{unitPrice}</p>
                  </div>
                </Col>
                {purchase_order.status == PurchaseOrderStatus.Draft && props.row.original.id && (
                  <Col sm={2}>
                    <a
                      className="button button--action button--action-approve"
                      title="Update Unit Price"
                      data-bs-toggle="tooltip"
                      onClick={() => {
                        setPurchaseOrderItem(props.row.original);
                        setShowUpdateUnitPriceModal(!showUpdateUnitPriceModal);
                      }}
                    >
                      <SvgIcon name="edit" className="h5" />
                    </a>
                  </Col>
                )}
              </Row>
            </Container>
          );
        }
      },
      {
        id: "total",
        Header: "Total",
        accessor: "subtotal_amount",
        Cell: (props) => {
          const index = props.row.index;
          const quantity = parseFloat(watch(`${fieldArrayName}.${index}.quantity`) || 0);
          const unitPrice = parseFloat(props.row.original.unit_price || getValues(`${fieldArrayName}.${index}.unit_price`));

          return (
            <div title={formatter.format(quantity * unitPrice)} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{formatter.format(quantity * unitPrice)}</p>
            </div>
          );
        }
      },
      {
        accessor: "actions",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          const index = props.row.index;
          const id = props.row.values.id;
          if (purchase_order.status == PurchaseOrderStatus.Draft) {
            return (
              <Button
                className="button--danger"
                icon="minus"
                iconProps={{ className: "svg-icon h5" }}
                onClick={() => {
                  if (id) {
                    const item = purchaseOrderItems.find((item) => item?.id == id);
                    if (item) setItemsForDestruction((value) => [...value, { ...item, _destroy: "1" }]);

                    setPurchaseOrderItems((items) => items.filter((item) => item.id != id));
                  }
                  resetField(`${fieldArrayName}.${index}`, { defaultValue: {} });
                  remove(index);
                  calculateTotal();
                }}
              />
            );
          } else {
            return <></>;
          }
        }
      }
    ],
    []
  );

  const withItemsForDestruction = (data) => {
    const { purchase_order_items } = data;

    return { ...data, purchase_order_items: [...purchase_order_items, ...itemsForDestruction] };
  };

  const save = async (data): Promise<unknown> => {
    try {
      await api.put(admin_purchase_order_path(data.id), {
        purchase_order: {
          id: data.id,
          allow_partial_delivery: data.allow_partial_delivery,
          expected_delivery_date: data.expected_delivery_date
        },
        ...withItemsForDestruction(data)
      });

      navigateTo(admin_purchase_orders_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const placeOrder = async (data): Promise<unknown> => {
    try {
      await api.put(release_admin_purchase_order_path(data.id), {
        purchase_order: {
          id: data.id,
          allow_partial_delivery: data.allow_partial_delivery,
          expected_delivery_date: data.expected_delivery_date
        },
        ...withItemsForDestruction(data)
      });

      navigateTo(admin_purchase_orders_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowPlaceOrderConfirmationModal(!showPlaceOrderConfirmationModal);
    }
  };

  const updatePrice = async (data): Promise<unknown> => {
    await api.put(update_price_admin_purchase_order_path(purchase_order.id), {
      ...data,
      purchase_order_item_id: purchaseOrderItem.id
    });

    const updatedPurchaseOrderItems = purchaseOrderItems.map((item) => {
      if (item.id == purchaseOrderItem.id) {
        return {
          ...purchaseOrderItem,
          unit_price: data.new_price
        };
      }
      return item;
    });

    setPurchaseOrderItems(updatedPurchaseOrderItems);
    setValue(fieldArrayName, updatedPurchaseOrderItems);
  };

  const cancelOrder = async (data): Promise<unknown> => {
    try {
      await api.put(cancel_admin_purchase_order_path(data.id));
      navigateTo(admin_purchase_orders_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowCancelOrderConfirmationModal(!showCancelOrderConfirmationModal);
    }
  };

  const closeOrder = async (data): Promise<unknown> => {
    try {
      await api.put(close_partially_delivered_admin_purchase_order_path(data.id));
      navigateTo(admin_purchase_orders_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowCloseOrderConfirmationModal(!showCloseOrderConfirmationModal);
    }
  };

  const onError = (data) => {
    console.error(data);
  };

  const adminFormActions = () => {
    return (
      <>
        {purchase_order.status == PurchaseOrderStatus.Draft && (
          <>
            <Button
              className="button--brand mr-3"
              disabled={isSubmitting || isValidating}
              icon="save"
              title="Save"
              type="button"
              onClick={handleSubmit(async (data) => await save(data), onError)}
            />
            {can_place_order && (
              <Button
                className="button--brand mr-3"
                disabled={isSubmitting || isValidating}
                icon="clipboard-list"
                title="Place Order"
                type="button"
                onClick={() => {
                  setValue("status", PurchaseOrderStatus.Open);
                  setShowPlaceOrderConfirmationModal(!showPlaceOrderConfirmationModal);
                }}
              />
            )}
          </>
        )}
        {purchase_order.status == PurchaseOrderStatus.Open && !purchase_order.has_deliveries && (
          <>
            <Button
              className="button--danger mr-3"
              disabled={isSubmitting || isValidating}
              icon="clipboard-list"
              title="Cancel Order"
              type="button"
              onClick={() => {
                setValue("status", PurchaseOrderStatus.Cancelled);
                setShowCancelOrderConfirmationModal(!showCancelOrderConfirmationModal);
              }}
            />
          </>
        )}
        {purchase_order.status == PurchaseOrderStatus.PartiallyDelivered && (
          <Button
            className="button--brand mr-3"
            disabled={isSubmitting || isValidating}
            icon="clipboard-list"
            title="Close Order"
            type="button"
            onClick={() => setShowCloseOrderConfirmationModal(!showCloseOrderConfirmationModal)}
          />
        )}
        {![PurchaseOrderStatus.Draft, PurchaseOrderStatus.Cancelled].includes(purchase_order.status) && (
          <>
            <Button
              className="button--primary"
              icon="printer"
              title="Print"
              type="button"
              onClick={() => window.open(print_admin_purchase_order_path(purchase_order))}
            />
          </>
        )}
      </>
    );
  };

  const siteFormActions = () => {
    return (
      <Button
        className="button--brand"
        disabled={isSubmitting || isValidating}
        icon="truck"
        title="Process Delivery"
        type="button"
        onClick={() => navigateTo(delivery_site_purchase_order_path(purchase_order.id))}
      />
    );
  };

  const canAppendItem = () => {
    if (purchase_order.id && purchase_order.status != PurchaseOrderStatus.Draft) return false;
    return fields.length < purchase_order.purchase_request_items.length + purchaseOrderItems.length;
  };

  React.useEffect(() => {
    reset({
      ...getValues(),
      purchase_order_items: fields.filter((field) => purchaseOrderItems.filter((item) => item.id == field.id))
    });
  }, [purchaseOrderItems]);

  return (
    <React.StrictMode>
      <PurchaseOrderContext.Provider value={{ selectedItems, setSelectedItems }}>
        {formErrors && formErrors.length != 0 && (
          <Alert>
            {formErrors.map((error, index) => {
              return <div key={index}>{error}</div>;
            })}
          </Alert>
        )}
        <Card>
          <Card.Header title="Purchase Order">
            <Button
              className="button--default mr-3"
              disabled={isSubmitting || isValidating}
              icon="arrow-left"
              title="Back"
              type="button"
              onClick={() => navigateTo(isSiteForm ? pending_site_deliveries_path() : admin_purchase_orders_path())}
            />
            {isSiteForm ? (can_receive ? siteFormActions() : "") : adminFormActions()}
          </Card.Header>
          <Card.Body>
            <Container fluid={true}>
              {purchase_order.status == PurchaseOrderStatus.Draft && (
                <Row className="mb-2">
                  <Col lg={6}>
                    <Row>
                      <Col>
                        <Controller
                          name="allow_partial_delivery"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              {...field}
                              label="Allow Partial Delivery"
                              info="Allow Partial Delivery"
                              checked={field.value}
                              value={field.value?.toString()}
                              errorMessage={errors.allow_partial_delivery?.message}
                            />
                          )}
                        />
                      </Col>
                      <Col>
                        <Controller
                          name="expected_delivery_date"
                          control={control}
                          render={({ field }) => (
                            <FieldGroup
                              label="Expected Delivery Date"
                              errorMessage={errors.expected_delivery_date?.message}
                              info="Date initially drafted plus the supplier lead time."
                            >
                              <DatePicker
                                selected={field.value ? moment(field.value).toDate() : field.value}
                                onChange={field.onChange}
                                selectsEnd
                                className="field"
                                placeholderText="Select Expected Delivery Date"
                                minDate={moment().toDate()}
                              />
                            </FieldGroup>
                          )}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
              {purchaseOrderItems.length > 0 ? (
                <>
                  <Table
                    columns={columns}
                    data={fields}
                    hiddenColumns={[...draftColumns, ...reorderingColumns, ...amountColumns]}
                    control={control}
                    register={register}
                    errors={errors}
                  />
                  {canAppendItem() && (
                    <Col className="d-flex justify-content-start">
                      <Button
                        className="button--brand mt-4"
                        title="Add Item"
                        icon="plus"
                        type="button"
                        onClick={() => {
                          append({
                            requested_quantity: 0,
                            drafted_quantity: 0,
                            ordered_quantity: 0,
                            unit_price: 0
                          });
                        }}
                      />
                    </Col>
                  )}
                </>
              ) : (
                <div className="text-center mt-4 mb-4">No available purchase order items.</div>
              )}
            </Container>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <Container fluid={true}>
              <Row>
                <Col>
                  {purchase_order.supplier.tax_category == "vatable" && (
                    <>
                      <Row className="mb-1">
                        <Col>{`Vatable Purchases (${formatter.format(subtotal)} / ${(vatablePurchasesRate * 100.0).toFixed()}%)`}</Col>
                        <Col className="text-right">
                          <h5>{formatter.format(vatablePurchases)}</h5>
                        </Col>
                      </Row>
                      <Row className="mb-1">
                        <Col>{`Value Added Tax (${formatter.format(vatablePurchases)} * ${valueAddedTaxRate * 100.0}%)`}</Col>
                        <Col className="text-right">
                          <h5>{formatter.format(valueAddedTax)}</h5>
                        </Col>
                      </Row>
                    </>
                  )}
                  <Row className="mb-1">
                    <Col>Total</Col>
                    <Col className="text-right">
                      <h5>{formatter.format(subtotal)}</h5>
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    {purchase_order.supplier.tax_category == "non_vatable" ? (
                      <Col>{`Creditable Withholding Tax (${formatter.format(subtotal)} * ${witholdingTaxRate * 100.0}%)`}</Col>
                    ) : (
                      <Col>{`Creditable Withholding Tax (${formatter.format(vatablePurchases)} * ${witholdingTaxRate * 100.0}%)`}</Col>
                    )}
                    <Col className="text-right">
                      <h5>({formatter.format(witholdingTax)})</h5>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <h4>Total Amount Payable</h4>
                    </Col>
                    <Col className="text-right">
                      <h4>{formatter.format(total)}</h4>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Card>
        <Modal dialogClassName="modal-dialog-centered" show={showPlaceOrderConfirmationModal}>
          <Modal.Header>{`Place Order: ${purchase_order.code}`}</Modal.Header>
          <Modal.Body>{`Are you sure you want to place order: ${purchase_order.code}?`}</Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() => {
                setShowPlaceOrderConfirmationModal(!showPlaceOrderConfirmationModal);
              }}
              icon="times"
              title="Cancel"
            />
            <Button
              className="button--success"
              icon="save"
              title="Yes, place order."
              onClick={() => {
                handleSubmit(async (data) => await placeOrder(data), setShowPlaceOrderConfirmationModal(!showPlaceOrderConfirmationModal))();
              }}
            />
          </Modal.Footer>
        </Modal>
        <Modal dialogClassName="modal-dialog-centered" show={showCancelOrderConfirmationModal}>
          <Modal.Header>{`Cancel Order: ${purchase_order.code}`}</Modal.Header>
          <Modal.Body>{`Are you sure you want to cancel order: ${purchase_order.code}?`}</Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() => {
                setShowCancelOrderConfirmationModal(!showCancelOrderConfirmationModal);
              }}
              icon="times"
              title="Cancel"
            />
            <Button
              className="button--danger"
              icon="cancel"
              title="Yes, cancel order."
              onClick={() => {
                handleSubmit(async (data) => await cancelOrder(data), setShowCancelOrderConfirmationModal(!showCancelOrderConfirmationModal))();
              }}
            />
          </Modal.Footer>
        </Modal>
        <Modal dialogClassName="modal-dialog-centered" show={showCloseOrderConfirmationModal}>
          <Modal.Header>{`Close Order: ${purchase_order.code}`}</Modal.Header>
          <Modal.Body>{`Are you sure you want to close order: ${purchase_order.code}?`}</Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() => {
                setShowCloseOrderConfirmationModal(!showCloseOrderConfirmationModal);
              }}
              icon="times"
              title="Cancel"
            />
            <Button
              className="button--success"
              icon="save"
              title="Yes, close order."
              disabled={isSubmitting || isValidating}
              onClick={() => {
                handleSubmit(
                  async (data) => await closeOrder(data),
                  (data) => {
                    console.error(data);
                    setShowCloseOrderConfirmationModal(!showCloseOrderConfirmationModal);
                  }
                )();
              }}
            />
          </Modal.Footer>
        </Modal>
        {purchaseOrderItem && (
          <ItemPriceUpdate
            itemCode={purchaseOrderItem.item_code}
            itemName={purchaseOrderItem.item_name}
            currentPrice={purchaseOrderItem.unit_price}
            show={showUpdateUnitPriceModal}
            onClose={() => setShowUpdateUnitPriceModal(!showUpdateUnitPriceModal)}
            onSubmit={(data) => {
              updatePrice(data)
                .then(() => {
                  calculateTotal();
                })
                .catch((error) => {
                  console.error(error);
                  alert("Something went wrong.");
                })
                .finally(() => {
                  setShowUpdateUnitPriceModal(!showUpdateUnitPriceModal);
                });
            }}
          />
        )}
      </PurchaseOrderContext.Provider>
    </React.StrictMode>
  );
};

export default PurchaseOrderForm;
